import React from "react";
import { useTranslation } from "react-i18next";
import Callout from "components/callout/callout";
import Section from "components/layout/section";
import TableListRow from "components/list/tableList/tableListRow";
import TableListCell from "components/list/tableList/tableListCell";
import TableList from "components/list/tableList/tableList";
import Tag from "components/tags/tag";
import IconButton from "../buttons/iconButton";
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg'
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import useAccess from "@/hooks/useAccess";
import { AccessInvitation, AccessStatus, FormStatus } from "@/types";
import useLayout from "@/hooks/useLayout";

interface AccessInvitationListProps {
    displayHelper?: boolean;
    storageId?: string;
}

const AccessInvitationList: React.FC<AccessInvitationListProps> = ({ storageId }) => {
    const { t } = useTranslation();
    const { openDialog } = useLayout();

    const {
        accessInvitations,
        accessStatus,
    } = useAccess({ storageId });

    const invitationStorageId = (doc: AccessInvitation) => doc.storage?.storageId || '';

    const handleResponseClick = (doc: AccessInvitation, accept: boolean) => {
        const resolvedStorageId = invitationStorageId(doc);
        if (!resolvedStorageId || !doc.id) return;
        openDialog('respond_access', {
            storageId: resolvedStorageId,
            accessId: doc.id,
            accept
        });
    };

    const handleDeleteClick = (doc: AccessInvitation) => {
        const resolvedStorageId = invitationStorageId(doc);
        if (!resolvedStorageId || !doc.id) return;
        openDialog('delete_access', { storageId: resolvedStorageId, accessId: doc.id });
    };

    const renderStatus = (doc: AccessInvitation) => (
        <>
            {doc.status === AccessStatus.PENDING && <Tag colorScheme="yellow">{t('titlePending')}</Tag>}
            {doc.status === AccessStatus.ACCEPTED && <Tag colorScheme="green">{t('titleAccepted')}</Tag>}
            {doc.status === AccessStatus.DECLINED && <Tag colorScheme="red">{t('titleDeclined')}</Tag>}
        </>
    );

    const renderActions = (doc: AccessInvitation) => {
        const isOwner = doc.role === 'owner';
        if (isOwner) return null;
        return (
            <>
                {doc.status === AccessStatus.PENDING && (
                    <>
                        <IconButton
                            icon={<CheckIcon />}
                            size="small"
                            colorScheme="green"
                            onClick={() => handleResponseClick(doc, true)}
                        />
                        <IconButton
                            icon={<CloseIcon />}
                            size="small"
                            colorScheme="red"
                            onClick={() => handleResponseClick(doc, false)}
                        />
                    </>
                )}
                <IconButton
                    icon={<DeleteIcon />}
                    size="small"
                    colorScheme="red"
                    onClick={() => handleDeleteClick(doc)}
                />
            </>
        );
    };

    if (accessStatus === FormStatus.ERROR) {
        return <Callout type="error">{t('errorLoadingData')}</Callout>;
    }

    if (accessStatus === FormStatus.SUCCESS && accessInvitations.length === 0) {
        return (
            <Section>
                <Callout type="warning">
                    {t('calloutNoAccessInvitations')}
                </Callout>
            </Section>
        );
    }

    return (
        <TableList>
            <TableListRow isHeader>
                <TableListCell>{t('titleStorage')}</TableListCell>
                <TableListCell>{t('titleRole')}</TableListCell>
                <TableListCell>{t('titleMessage')}</TableListCell>
                <TableListCell>{t('titleStatus')}</TableListCell>
                <TableListCell></TableListCell>
            </TableListRow>
            {accessStatus === FormStatus.LOADING &&
                Array.from({ length: 3 }).map((_, index) => (
                    <TableListRow key={index}>
                        <TableListCell><p className="skeleton"></p></TableListCell>
                        <TableListCell><p className="skeleton"></p></TableListCell>
                        <TableListCell><p className="skeleton"></p></TableListCell>
                        <TableListCell><p className="skeleton"></p></TableListCell>
                        <TableListCell><p className="skeleton"></p></TableListCell>
                    </TableListRow>
                ))
            }
            {accessInvitations.map((doc) => (
                <TableListRow key={doc.id} hoverEffect={false}>
                    <TableListCell>
                        <span className="truncate">{doc.storage?.storageName || t('titleStorage')}</span>
                    </TableListCell>
                    <TableListCell className="capitalize">{t(doc.role)}</TableListCell>
                    <TableListCell>
                        <span className="truncate max-w-xs">{doc.invitation.message}</span>
                    </TableListCell>
                    <TableListCell>
                        {renderStatus(doc)}
                    </TableListCell>
                    <TableListCell justifyContent="end">
                        {renderActions(doc)}
                    </TableListCell>
                </TableListRow>
            ))}
        </TableList>
    );
};

export default AccessInvitationList;
